import type {
  InvoiceEmailRecipient,
  InvoiceEmailRecipientRole,
} from '@/_pages/invoices/shared/get-invoice-email-recipients';
import {
  getContactPrimaryEmail,
  getInvoiceEmailRecipients,
  orderInvoiceEmailRecipients,
} from '@/_pages/invoices/shared/get-invoice-email-recipients';
import type { InvoiceQuerySchema } from '@/_pages/invoices/shared/schemas/invoices/invoice-query-schema';
import { nameResolver } from '@/_shared';

import { type EmailMetadataSchema, parseJsonField } from './schemas/email-history-json-schema';
import type { EmailHistoryQuerySchema, EmailHistoryRecipientContactSchema } from './schemas/email-history-query-schema';

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

/** Supports plain addresses and `Display Name <email@domain.com>` from metadata. */
function parseToEmailAddress(value: string): string {
  const trimmed = value.trim();
  const angleMatch = trimmed.match(/<([^>]+)>/);
  return normalizeEmail(angleMatch?.[1] ?? trimmed);
}

function buildContactByEmailMap(
  contacts: EmailHistoryRecipientContactSchema[]
): Map<string, EmailHistoryRecipientContactSchema> {
  const map = new Map<string, EmailHistoryRecipientContactSchema>();
  for (const contact of contacts) {
    for (const entry of contact.emails ?? []) {
      const key = normalizeEmail(entry.email ?? '');
      if (key && !map.has(key)) {
        map.set(key, contact);
      }
    }
    const primary = getContactPrimaryEmail(contact)?.trim();
    if (primary) {
      const key = normalizeEmail(primary);
      if (!map.has(key)) {
        map.set(key, contact);
      }
    }
  }
  return map;
}

function buildRoleLookup(invoice: InvoiceQuerySchema): Map<number, InvoiceEmailRecipientRole> {
  const lookup = new Map<number, InvoiceEmailRecipientRole>();
  for (const recipient of getInvoiceEmailRecipients(invoice)) {
    lookup.set(recipient.id, recipient.role);
  }
  return lookup;
}

function resolveRole(
  recipient: InvoiceEmailRecipient,
  roleByContactId: Map<number, InvoiceEmailRecipientRole>,
  invoiceRecipients: InvoiceEmailRecipient[]
): InvoiceEmailRecipientRole {
  const fromId = roleByContactId.get(recipient.id);
  if (fromId) {
    return fromId;
  }

  const emailKey = recipient.email ? parseToEmailAddress(recipient.email) : '';
  if (emailKey) {
    const fromEmail = invoiceRecipients.find((r) => r.email && parseToEmailAddress(r.email) === emailKey)?.role;
    if (fromEmail) {
      return fromEmail;
    }
  }

  return 'notification';
}

function sortByInvoiceRecipientOrder(
  recipients: InvoiceEmailRecipient[],
  invoiceRecipients: InvoiceEmailRecipient[]
): InvoiceEmailRecipient[] {
  const orderById = new Map(invoiceRecipients.map((r, index) => [r.id, index]));
  const orderByEmail = new Map(
    invoiceRecipients.filter((r) => r.email).map((r, index) => [parseToEmailAddress(r.email!), index])
  );
  const unknownOrderBase = invoiceRecipients.length;

  const sortIndex = (recipient: InvoiceEmailRecipient, sourceIndex: number) => {
    const byId = orderById.get(recipient.id);
    if (byId !== undefined) return byId;

    if (recipient.email) {
      const byEmail = orderByEmail.get(parseToEmailAddress(recipient.email));
      if (byEmail !== undefined) return byEmail;
    }

    return unknownOrderBase + sourceIndex;
  };

  const sortRoleGroup = (role: InvoiceEmailRecipientRole) =>
    recipients
      .map((recipient, sourceIndex) => ({ recipient, sourceIndex }))
      .filter(({ recipient }) => recipient.role === role)
      .sort((a, b) => sortIndex(a.recipient, a.sourceIndex) - sortIndex(b.recipient, b.sourceIndex))
      .map(({ recipient }) => recipient);

  return [...sortRoleGroup('billing'), ...sortRoleGroup('notification')];
}

/** Billing recipients from `email_metadata.to`, enriched with contact records when available. */
export function getEmailHistoryRecipients(
  row: EmailHistoryQuerySchema,
  invoice?: InvoiceQuerySchema | null
): InvoiceEmailRecipient[] {
  const metadata = parseJsonField<EmailMetadataSchema>(row.email_metadata);
  const toEmails = (metadata?.to ?? []).map((e) => e?.trim()).filter((e): e is string => Boolean(e));

  if (toEmails.length === 0) {
    return [];
  }

  const contactByEmail = buildContactByEmailMap(row.email_recipient_contacts ?? []);
  const invoiceRecipients = invoice ? getInvoiceEmailRecipients(invoice) : [];
  const roleByContactId = invoice ? buildRoleLookup(invoice) : new Map<number, InvoiceEmailRecipientRole>();

  const recipients = toEmails.map((rawEmail, index) => {
    const contact = contactByEmail.get(parseToEmailAddress(rawEmail));

    if (contact && typeof contact.id === 'number' && contact.id > 0) {
      const recipient: InvoiceEmailRecipient = {
        id: contact.id,
        name: nameResolver(contact.first_name, contact.last_name, rawEmail),
        email: rawEmail,
        role: 'notification',
        avatarUrl: contact.profile_image?.at(0)?.url ?? null,
      };
      recipient.role = invoice
        ? resolveRole(recipient, roleByContactId, invoiceRecipients)
        : index === 0
          ? 'billing'
          : 'notification';
      return recipient;
    }

    return {
      id: -(index + 1),
      name: rawEmail,
      email: rawEmail,
      role: (index === 0 ? 'billing' : 'notification') as InvoiceEmailRecipientRole,
    };
  });

  if (invoice && invoiceRecipients.length > 0) {
    return sortByInvoiceRecipientOrder(recipients, invoiceRecipients);
  }

  return orderInvoiceEmailRecipients(recipients);
}
